import { useEffect, useRef, useState } from 'react'
import { getTimeline } from '../api/dataCorrectionApi'
import { classifyFailure } from '../model/apiFailure'
import { describeReadFailure, isTooManyRows } from '../model/messageCatalog'
import { parseIsoInstant } from '../model/timeGrid'
import { MIN_RANGE_MS, canGoNext, canQuery, halved, initialQuery, nextPeriod, previousPeriod, showCorrection, withFilterEmptied, withFilters, withPage, withView } from '../model/timelineQuery'

const NOT_FOUND_FILTER = { DATA_CORRECTION_DEVICE_NOT_FOUND: 'deviceId', DATA_CORRECTION_WORKER_NOT_FOUND: 'workerId' }

// The query of the screen, what the user can do with it (RN9-RN14) and the reading it triggers (RN15-RN20).
// `deviceNameRef.current(id)` names a machine for the messages (catalog, then earlier readings, '' when nobody knows).
export const useTimeline = ({ deviceNameRef }) => {
  const [query, setQuery] = useState(() => initialQuery(Date.now()))
  const [loaded, setLoaded] = useState({})
  const [loading, setLoading] = useState(false)
  const [notice, setNotice] = useState([])
  const [errors, setErrors] = useState([])
  const [reloadToken, setReloadToken] = useState(0)
  // «View on the timeline» (RN16): the instant the grid scrolls to once the reading of `from` is painted.
  const [focus, setFocus] = useState({ ms: 0, fromMs: 0, token: 0 })
  const queryRef = useRef(query)
  queryRef.current = query
  const aliveRef = useRef(false)
  const sequenceRef = useRef(0)

  useEffect(() => {
    // Switched on inside the effect (StrictMode mounts twice).
    aliveRef.current = true
    return () => { aliveRef.current = false }
  }, [])

  useEffect(() => {
    // Every request gets a number and only the answer of the LAST one is painted: `apiGet` cannot be aborted, so the
    // earlier ones are simply ignored when they arrive (RN15).
    const sequence = ++sequenceRef.current
    if (!canQuery(query)) {
      setLoading(false)
      return
    }
    setLoading(true)
    setErrors([])
    const isCurrent = () => aliveRef.current && sequence === sequenceRef.current
    getTimeline(query).then((response) => {
      if (!isCurrent()) return
      // `apiGet` resolves undefined when it already sent the app to /connection_error: say nothing, ask nothing (RN20).
      if (response === undefined) {
        setLoading(false)
        return
      }
      // A page past the last one comes back empty although the worker has machines: go back to page 0 (RN14).
      if (response.timelines.length === 0 && response.page.totalDevices > 0 && query.page > 0) {
        setQuery(withPage(query, 0))
        return
      }
      setLoaded({ query, response })
      setLoading(false)
    }).catch((error) => {
      if (!isCurrent()) return
      const failure = classifyFailure(error)
      const context = { deviceName: (id) => deviceNameRef.current(id) }
      if (isTooManyRows(failure) && query.to - query.from > MIN_RANGE_MS) {
        // Too dense (RH-8): half of the range keeping `to`, and ask again; the notice tells it (RN17).
        setNotice([describeReadFailure(failure, context)])
        setQuery(halved(query))
        return
      }
      setNotice([])
      setErrors([describeReadFailure(failure, { ...context, atMinimum: true })])
      // The machine or the worker does not exist (404): the filter is emptied and no block is left (RN19).
      const missing = NOT_FOUND_FILTER[failure.message]
      if (isTooManyRows(failure) || missing) setLoaded({})
      if (missing) setQuery((current) => withFilterEmptied(current, missing))
      setLoading(false)
    })
  }, [query, deviceNameRef, reloadToken])

  const asUser = (change) => {
    setNotice([])
    change()
  }

  return {
    query,
    focus,
    loaded,
    loading,
    messages: { notice, errors },
    canGoNext: canGoNext(query),
    handleFilters: (filters) => asUser(() => setQuery((current) => withFilters(current, filters, Date.now()))),
    handleView: (view) => asUser(() => setQuery((current) => withView(current, view))),
    handlePage: (page) => setQuery((current) => withPage(current, page)),
    // Asks the same query again (after applying, or when Sun says the timeline changed, RN67, RN73).
    handleReload: () => setReloadToken((token) => token + 1),
    // The query of a correction (story 211, RN16): by machine and worker, in a period that shows its stretch; the zoom is not touched.
    handleShowCorrection: ({ deviceId, workerId, window }) => {
      const next = showCorrection(queryRef.current, { deviceId, workerId, window }, Date.now())
      setNotice([])
      setQuery(next)
      setFocus((current) => ({ ms: parseIsoInstant(window.start).getTime(), fromMs: next.from, token: current.token + 1 }))
    },
    handlePrevious: () => asUser(() => setQuery((current) => previousPeriod(current))),
    handleNext: () => asUser(() => setQuery((current) => nextPeriod(current, Date.now())))
  }
}
